import React, { useState, useEffect } from 'react';

const parseDateInput = (input) => {
  if (!input) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
  const match = input.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (!match) return input;
  let [, month, day, year] = match;
  if (year.length === 2) year = '20' + year;
  month = month.padStart(2, '0');
  day = day.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return '';
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

const IndividualGAD7Form = ({ onSave, onCancel, existingData = [] }) => {
  const [rows, setRows] = useState([{ clientId: '', date: '', gad7Score: '' }]);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState(null);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const rowRefs = React.useRef({});
  const pendingScrollRef = React.useRef(false);

  useEffect(() => {
    if (existingData.length > 0) {
      setRows(existingData.map(d => ({
        clientId: d.clientId || '',
        date: d.date || '',
        gad7Score: d.gad7Score !== undefined ? String(d.gad7Score) : ''
      })));
    }
  }, [existingData]);

  const addRow = () => {
    pendingScrollRef.current = true;
    setRows([...rows, { clientId: '', date: '', gad7Score: '' }]);
  };

  const addMultipleRows = (count) => {
    pendingScrollRef.current = true;
    const newRows = Array.from({ length: count }, () => ({ clientId: '', date: '', gad7Score: '' }));
    setRows([...rows, ...newRows]);
  };

  // Auto-scroll to the newly added row(s) after they render
  useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const el = rowRefs.current[rows.length - 1];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [rows.length]);

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== index));
    setErrors(prev => {
      const updated = {};
      Object.keys(prev).forEach(key => {
        const i = parseInt(key);
        if (i < index) updated[i] = prev[i];
        else if (i > index) updated[i - 1] = prev[i];
      });
      return updated;
    });
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
    if (errors[index]) {
      const rowErrors = { ...errors[index] };
      delete rowErrors[field];
      setErrors({ ...errors, [index]: Object.keys(rowErrors).length ? rowErrors : undefined });
    }
  };

  // Auto-fill client ID from the row above
  const handleClientIdBlur = (index) => {
    if (index > 0 && !rows[index].clientId.trim() && rows[index - 1].clientId.trim()) {
      updateRow(index, 'clientId', rows[index - 1].clientId);
    }
  };

  const handleSearch = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return;
    const matchIndex = rows.findIndex(row =>
      row.clientId.trim().toLowerCase() === query
    );
    if (matchIndex !== -1) {
      setSearchStatus('found');
      setHighlightedRow(matchIndex);
      rowRefs.current[matchIndex]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setSearchStatus('notfound');
      setTimeout(() => setSearchStatus(null), 2500);
    }
  };

  const validate = () => {
    const newErrors = {};
    rows.forEach((row, i) => {
      const rowErrors = {};
      if (!row.clientId.trim()) rowErrors.clientId = 'Required';
      if (!row.date || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) rowErrors.date = 'Enter date (e.g. 9/18/23)';
      const score = parseInt(row.gad7Score, 10);
      if (row.gad7Score === '' || isNaN(score) || score < 0 || score > 21) {
        rowErrors.gad7Score = 'Must be 0-21';
      }
      if (Object.keys(rowErrors).length) newErrors[i] = rowErrors;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = rows.map(r => ({
      clientId: r.clientId.trim(),
      date: r.date,
      gad7Score: parseInt(r.gad7Score, 10)
    }));
    onSave(data);
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Enter GAD-7 Tracking Data</h2>
      <p className="text-gray-600 mb-1 text-sm">
        Enter client IDs, assessment dates, and GAD-7 scores (0-21).
      </p>
      <p className="text-gray-500 mb-4 text-xs italic">
        Multiple rows per client are expected — each row is one assessment on a specific date.
        Client ID auto-fills from the row above if left blank.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={addRow} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold">
          + Add Row
        </button>
        <button onClick={() => addMultipleRows(5)} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-semibold">
          + Add 5 Rows
        </button>
        <button onClick={() => addMultipleRows(10)} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-semibold">
          + Add 10 Rows
        </button>

        {/* Search Client ID */}
        <div className="flex items-center gap-1 ml-auto">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSearchStatus(null); setHighlightedRow(null); }}
              onFocus={() => setHighlightedRow(null)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search Client ID..."
              className={`pl-8 pr-3 py-2 text-sm border rounded-lg w-48 focus:outline-none focus:ring-2 transition-colors ${
                searchStatus === 'notfound'
                  ? 'border-red-400 bg-red-50 focus:ring-red-300'
                  : searchStatus === 'found'
                  ? 'border-green-400 bg-green-50 focus:ring-green-300'
                  : 'border-gray-300 focus:ring-blue-300'
              }`}
            />
            {searchStatus === 'notfound' && (
              <span className="absolute -bottom-5 left-0 text-xs text-red-600 whitespace-nowrap">No match found</span>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Go
          </button>
        </div>
      </div>

      {/* Column headers */}
      <div className="flex gap-3 items-center mb-2 text-xs font-semibold text-gray-500 uppercase">
        <span className="w-8 text-right">#</span>
        <span className="flex-1">Client ID</span>
        <span className="w-40">Assessment Date</span>
        <span className="w-28">GAD-7 Score</span>
        <span className="w-6"></span>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto mb-6">
        {rows.map((row, i) => (
          <div
            key={i}
            ref={el => rowRefs.current[i] = el}
            className={`flex gap-3 items-start rounded-lg transition-colors duration-300 ${highlightedRow === i ? 'bg-yellow-100 ring-2 ring-yellow-400' : ''}`}
          >
            <span className="text-gray-400 text-sm mt-2 w-8 text-right">{i + 1}.</span>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Client ID"
                value={row.clientId}
                onChange={e => updateRow(i, 'clientId', e.target.value)}
                onBlur={() => handleClientIdBlur(i)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[i]?.clientId ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors[i]?.clientId && <p className="text-red-500 text-xs mt-1">{errors[i].clientId}</p>}
            </div>
            <div className="w-40">
              <input
                type="text"
                placeholder="MM/DD/YYYY"
                value={row.date ? formatDateForDisplay(row.date) : ''}
                onChange={e => updateRow(i, 'date', e.target.value)}
                onBlur={e => {
                  const parsed = parseDateInput(e.target.value);
                  updateRow(i, 'date', parsed);
                }}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[i]?.date ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors[i]?.date && <p className="text-red-500 text-xs mt-1">{errors[i].date}</p>}
            </div>
            <div className="w-28">
              <input
                type="number"
                min="0"
                max="21"
                placeholder="0-21"
                value={row.gad7Score}
                onChange={e => updateRow(i, 'gad7Score', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errors[i]?.gad7Score ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors[i]?.gad7Score && <p className="text-red-500 text-xs mt-1">{errors[i].gad7Score}</p>}
            </div>
            <button
              onClick={() => removeRow(i)}
              disabled={rows.length <= 1}
              className="mt-1 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-4 border-t">
        <button onClick={handleSave} className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
          Save Data ({rows.length} records)
        </button>
        <button onClick={onCancel} className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default IndividualGAD7Form;
